/**
 * As linhas das caixas de conferência das importações em PDF.
 *
 * Papel: aqui ficam a linha que a conferência mostra — a marca e o valor como
 * está escrito no campo —, o total selecionado, as linhas que vão para a API e
 * o aviso de quantos lançamentos nasceram, com o plural da origem.
 *
 * Como usar: a caixa monta as linhas por `linhasDaConferencia` com os valores
 * lidos do PDF, guarda-as no estado, e na confirmação pede `linhasConfirmadas`,
 * que deixam de fora as desmarcadas e as que não valem mais que zero. O total
 * selecionado soma todas as marcadas, inclusive as de valor zero, como o
 * `atualizarTotal…` da origem.
 */
import { lerValorDigitado } from '@/shared/lib/mascara';

/** Uma linha da conferência: a marca e o valor como o campo o mostra. */
export interface LinhaDaConferencia {
  marcada: boolean;
  /** O valor escrito, com a máscara de centavos. */
  valor: string;
}

/**
 * As linhas de uma conferência, todas marcadas.
 *
 * @param valores - Os valores lidos do PDF, na ordem das linhas.
 * @returns Uma linha por valor, com o valor em `pt-BR` e duas casas.
 */
export function linhasDaConferencia(valores: readonly number[]): LinhaDaConferencia[] {
  return valores.map((valor) => ({
    marcada: true,
    valor: valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  }));
}

/**
 * O "Total selecionado" da conferência.
 *
 * @param linhas - As linhas como estão na tela.
 * @returns A soma dos valores das linhas marcadas.
 */
export function totalSelecionado(linhas: readonly LinhaDaConferencia[]): number {
  return linhas.reduce(
    (soma, linha) => (linha.marcada ? soma + lerValorDigitado(linha.valor) : soma),
    0,
  );
}

/**
 * As linhas que a confirmação manda.
 *
 * @param linhas - As linhas como estão na tela.
 * @returns O índice de cada linha marcada com valor maior que zero e o valor
 *   lido, na ordem da tela.
 */
export function linhasConfirmadas(
  linhas: readonly LinhaDaConferencia[],
): { indice: number; valor: number }[] {
  return linhas.flatMap((linha, indice) => {
    const valor = lerValorDigitado(linha.valor);
    return linha.marcada && valor > 0 ? [{ indice, valor }] : [];
  });
}

/**
 * O aviso de quantos lançamentos uma importação criou (l. 4384, 4548).
 *
 * @param criados - Quantos a API criou.
 * @param origem - "de folha" ou "da guia DAE".
 * @returns `✓ 2 lançamentos de folha criados como Pendente`, com o "s" só
 *   quando são mais de um.
 */
export function avisoDosCriados(criados: number, origem: 'de folha' | 'da guia DAE'): string {
  const s = criados > 1 ? 's' : '';
  return `✓ ${String(criados)} lançamento${s} ${origem} criado${s} como Pendente`;
}
